import { useEffect, useMemo, useState } from 'react';
import type { FactoryAgentSummary } from '@reef/schema';
import { api } from '../api.ts';

/**
 * The seven agents, as one table.
 *
 * A table rather than a grid of cards, because the question this screen answers
 * is comparative: which agent owns this, what is it allowed to reach, and which
 * of them is running on something other than the default model. Cards make you
 * read seven things to compare one column.
 *
 * The instructions column is the important one and is deliberately the widest.
 * An agent here *is* its instructions — the skill is the committed document
 * that governs every run — so a studio that showed a name and a model and hid
 * the prose would be hiding the only part a human is meant to approve.
 */
export function FactoryAgentsView() {
  const [agents, setAgents] = useState<FactoryAgentSummary[]>([]);
  const [present, setPresent] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<FactoryAgentSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .factoryAgents()
      .then((result) => {
        setAgents(result.agents);
        setPresent(result.workflowsPresent);
      })
      .catch((caught) => setError((caught as Error).message));
  }, []);

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return agents;
    return agents.filter(
      (agent) =>
        agent.name.toLowerCase().includes(needle) ||
        agent.responsibility.toLowerCase().includes(needle) ||
        agent.instructions.toLowerCase().includes(needle),
    );
  }, [agents, filter]);

  return (
    <div className={`studio ${selected ? 'with-detail' : ''}`}>
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Agents</span>
        </span>
        <span className="spacer" />
        <a
          className="studio-head-link"
          href="https://github.com/MartinRoberts-Fountain/reef/blob/main/docs/factory/README.md"
          target="_blank"
          rel="noreferrer"
        >
          Agents documentation
        </a>
      </header>

      <div className="studio-split detail">
        <section className="studio-card grow">
          <div className="studio-card-body">
            <input
              className="studio-filter"
              placeholder="Filter by name or instructions"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              aria-label="Filter agents"
            />

            {error && <div className="banner danger">{error}</div>}

            {present.length === 0 && !error && (
              <div className="banner">
                <h3>No factory workflows are deployed here yet</h3>
                <p style={{ margin: 0 }}>
                  The seven agents below are defined by their committed skills. Run{' '}
                  <code>pnpm seed:local</code> to load the factory's workflows, and the tools and
                  step counts on each row will fill in from the documents.
                </p>
              </div>
            )}

            <table className="studio-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Instructions</th>
                  <th scope="col">Model</th>
                  <th scope="col" className="numeric" title="Workflows with a step running this agent">
                    Flows
                  </th>
                  <th scope="col" className="numeric" title="Steps that run this agent">
                    Steps
                  </th>
                  <th scope="col" className="numeric" title="Connection mounts the agent can reach">
                    Tools
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((agent) => (
                  <tr
                    key={agent.id}
                    aria-current={selected?.id === agent.id}
                    onClick={() => setSelected(agent)}
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelected(agent);
                      }
                    }}
                  >
                    <td className="studio-agent-name">{agent.name}</td>
                    <td className="studio-agent-instructions">{firstLine(agent.instructions)}</td>
                    <td className="studio-agent-model">
                      <span className="model-mark" aria-hidden="true" />
                      {agent.model.replace(/^anthropic\//, '')}
                    </td>
                    <td className="numeric">{dash(agent.workflows.length)}</td>
                    <td className="numeric">{dash(agent.stepCount)}</td>
                    <td className="numeric">{dash(agent.tools.length)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {shown.length === 0 && !error && present.length > 0 && (
              <p className="hint">No agent matches “{filter}”.</p>
            )}
          </div>
        </section>

        {selected && (
          <aside className="studio-detail">
            <div className="studio-card-head">
              <h2>{selected.name}</h2>
              <button className="ghost" onClick={() => setSelected(null)} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="studio-card-body">
              <dl className="studio-facts">
                <dt>Responsibility</dt>
                <dd>{selected.responsibility}</dd>
                <dt>Triggered by</dt>
                <dd>{selected.triggeredBy}</dd>
                <dt>Produces</dt>
                <dd>{selected.produces}</dd>
                <dt>Skill</dt>
                <dd>
                  <code>{selected.skill}</code>
                </dd>
                <dt>Model</dt>
                <dd>
                  <code>{selected.model}</code>
                </dd>
                <dt>Tools</dt>
                <dd>
                  {selected.tools.length === 0 ? (
                    <span className="hint">None — no deployed step runs this agent yet.</span>
                  ) : (
                    selected.tools.map((tool) => (
                      <span key={tool} className="chip">
                        {tool}
                      </span>
                    ))
                  )}
                </dd>
                <dt>Workflows</dt>
                <dd>
                  {selected.workflows.length === 0 ? (
                    <span className="hint">Not deployed here.</span>
                  ) : (
                    selected.workflows.map((id) => (
                      <span key={id} className="chip">
                        {id}
                      </span>
                    ))
                  )}
                </dd>
              </dl>

              <p className="section-title">Instructions</p>
              {/*
                The whole skill, not an excerpt. This is the document a human
                approves instead of approving every pull request the agent
                opens, and an approval given to a summary is not an approval.
              */}
              <pre className="studio-instructions">{selected.instructions}</pre>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

/** The first sentence of the instructions, for the table's one-line cell. */
function firstLine(instructions: string): string {
  const line = instructions.split('\n').find((entry) => entry.trim().length > 0) ?? '';
  return line.trim();
}

/** An em dash reads as "none"; a zero reads as a measurement that came back 0. */
function dash(count: number): string {
  return count === 0 ? '–' : String(count);
}
